import { t } from "i18next";
import { Modal, ModalBody } from "reactstrap";

import Loader from "@components/shared/Loader/Loader";
import { USER_TYPE } from "@constants/Bookings";
import { useAddGuests } from "@hooks/useAddGuests/useAddGuests";
import { AddGuestsProps } from "@models/components/booking.model";
import commonUtils from "@utils/common.utils";

import ProfileCard from "../ProfileCard/ProfileCard";

import AddGuestButton from "./AddGuestButton/AddGuestButton";
import GuestForm from "./GuestForm/GuestForm";
import GuestMinMaxBadge from "./GuestMinMaxBadge/GuestMinMaxBadge";
import MemberSearch from "./MemberSearch/MemberSearch";

const AddGuests = ({ guestError, slotInfo }: AddGuestsProps) => {
  const { actions, config, data, setters, ui } = useAddGuests({
    slotInfo,
  });
  return (
    <>
      <div id="fg-booking-add-guests-section">
        <h4 className="mb-0 fg-booking-subheading py-4">
          {t("addGuest.addGuest")}

          <GuestMinMaxBadge
            allowBringGuest={data.allowBringGuest}
            slot={data.selectedSlot?.slot}
          />
        </h4>

        {ui.guestMessage ? (
          <div className="alert alert-danger small p-2 fg-booking-authenticated-banner">
            {ui.guestMessage}
          </div>
        ) : guestError ? (
          <div className="alert alert-danger small p-2 mb-3">{guestError}</div>
        ) : null}

        <div className="d-flex flex-wrap align-items-center gap-3 fg-booking-add-guests-list">
          {data.guestList.map(guest => (
            <ProfileCard
              key={guest.profileUuid}
              cardId={guest.profileUuid}
              handleRemoveCard={actions.handleRemoveGuest}
              cardName={commonUtils.getCardNameByType(guest)}
              errorMsg={data.checkparticipantErrors[guest.profileUuid]}
              disabled={data.isGuestAdditionDisabled}
            />
          ))}

          {!data.maxExceeded && (
            <AddGuestButton
              disabled={data.quotaExceeded || data.isGuestAdditionDisabled}
              onClick={actions.openAddGuestModal}
              label={t("addGuest.addUser")}
            />
          )}
        </div>
      </div>

      <hr className="my-3" />

      <Modal
        size="md"
        isOpen={ui.showAddGuestModal}
        toggle={actions.closeAddGuestModal}
        id="fg-booking-add-guest-modal"
        zIndex={1070}
      >
        <ModalBody className="text-center px-4 py-4">
          <h4 className="fw-bold">{t("addGuest.addGuest")}</h4>
          <p className="text-muted mb-4">{t("addGuest.addMembersOrGuests")}</p>

          <ul
            className="nav nav-tabs justify-content-center mb-3 d-flex"
            role="tablist"
          >
            {config.ADDGUESTMODALTABS.map(tab => (
              <li className="nav-item flex-fill" key={tab.id}>
                <button
                  type="button"
                  role="tab"
                  className={`w-100 bg-transparent border-0 p-2 fg-booking-add-guest-modal-tab ${
                    ui.activeTab === tab.id ? "active" : "text-muted"
                  } ${tab.disabled ? "disabled" : ""}`}
                  onClick={() => {
                    if (!tab.disabled) {
                      setters.setActiveTab(tab.id);
                      setters.resetGuestForm();
                    }
                  }}
                  disabled={tab.disabled}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>

          {ui.addGuestError !== "" && (
            <div className="alert alert-danger p-1 fg-FS-14">
              {ui.addGuestError}
            </div>
          )}
          {data.maxExceeded && ui.showMaxExceededMessage && (
            <div className="alert alert-danger p-1 fg-FS-14">
              {t("addGuest.maxGuestsExceeded", { max: data.maxParticipants })}
            </div>
          )}

          <div className="tab-content">
            {ui.activeTab === USER_TYPE.MEMBER && (
              <div className="tab-pane fade show active" role="tabpanel">
                <MemberSearch
                  selectedGuest={data.selectedGuest}
                  loadOptions={actions.loadOptions}
                  onChangeGuest={setters.setSelectedGuest}
                  clearError={() => setters.setAddGuestError("")}
                  placeholder={t("addGuest.searchMembers")}
                />
              </div>
            )}

            {ui.activeTab === USER_TYPE.GUEST && (
              <div className="tab-pane fade show active" role="tabpanel">
                <GuestForm
                  fields={config.ADDGUESTMODALFIELDS}
                  guestFormData={data.guestFormData}
                  setGuestFormData={setters.setGuestFormData}
                  emailError={ui.emailError}
                />
              </div>
            )}

            <div className="d-flex gap-3">
              <button
                className="btn fg-btn fg-btn-filled-darkblue w-50 mb-0"
                onClick={actions.handleAddGuest}
                disabled={ui.isAddGuestButtonDisabled}
              >
                {t(
                  ui.activeTab === USER_TYPE.MEMBER
                    ? "addGuest.addMember"
                    : "addGuest.addGuest"
                )}
              </button>

              <button
                className="btn fg-btn btn-outline-secondary w-50 mb-0"
                onClick={actions.closeAddGuestModal}
              >
                {t("addGuest.close")}
              </button>
            </div>
          </div>
        </ModalBody>
      </Modal>

      <Loader isLoading={ui.isLoading} />
    </>
  );
};

export default AddGuests;
